import React from 'react';
import PropTypes from 'prop-types';

import { FIELD_COMPONENTS } from '@atlas/react-forms';

import CheckboxField from 'common/Form/Fields/CheckboxField';
import ComplexPasswordField from 'common/Form/Fields/ComplexPasswordField';
import DaypickerField from 'common/Form/Fields/DaypickerField';
import DepositField from 'common/Form/Fields/DepositField';
import FileField from 'common/Form/Fields/FileField';
import NumberField from 'common/Form/Fields/NumberField/NumberField';
import PasswordField from 'common/Form/Fields/PasswordField';
import PhoneField from 'common/Form/Fields/PhoneField';
import RadioSelectField from 'common/Form/Fields/RadioSelectField';
import SelectField from 'common/Form/Fields/SelectField';
import SelectorField from 'common/Form/Fields/SelectorField/SelectorField';
import TextField from 'common/Form/Fields/TextField';

import EmailWithVerification from '../Fields/EmailWithVerificationField';

const {
  CHECKBOX,
  COMPLEX_PASSWORD,
  CURRENCYPICKER,
  EMAIL_WITH_VERIFICATION,
  FILE,
  LIST,
  NUMBER,
  PASSWORD,
  PHONE,
  PHONE_WITH_VERIFICATION,
  SELECT,
  SELECTOR,
  SMS_CODE,
  STRING,
  TEXT,
  TIMESTAMP,
} = FIELD_COMPONENTS;

/**
 * Keeps a field in the form state (so its value is still submitted in the
 * payload) while preventing it from rendering for the user. The form library
 * renders every field regardless of the `hidden` flag, so we enforce it here.
 * Used for the social (Google) registration flow, where the password is
 * provided by the backend and must be sent but never shown.
 */
const withHiddenField = Component => {
  const HiddenAwareField = props => {
    if (props.field?.hidden) {
      return null;
    }

    return <Component {...props} />;
  };

  HiddenAwareField.displayName = `WithHiddenField(${
    Component.displayName || Component.name || 'Field'
  })`;

  HiddenAwareField.propTypes = {
    field: PropTypes.object,
  };

  return HiddenAwareField;
};

const rawFieldComponents = {
  [CHECKBOX]: CheckboxField,
  [COMPLEX_PASSWORD]: ComplexPasswordField,
  [CURRENCYPICKER]: SelectField,
  [EMAIL_WITH_VERIFICATION]: EmailWithVerification,
  [FILE]: FileField,
  [LIST]: SelectField,
  [NUMBER]: NumberField,
  [PASSWORD]: PasswordField,
  [PHONE]: PhoneField,
  [PHONE_WITH_VERIFICATION]: PhoneField,
  [SELECT]: SelectField,
  [SELECTOR]: props => <SelectorField {...{ fieldComponents }} {...props} />,
  [SMS_CODE]: TextField,
  [STRING]: TextField,

  // Backend Form fields
  [TEXT]: TextField,
  [TIMESTAMP]: DaypickerField,
  checkbox: CheckboxField,
  countrypicker: SelectField,
  daypicker: DaypickerField,
  depositInput: DepositField,
  file: FileField,
  moneyInput: DepositField,
  password: PasswordField,
  radioselect: RadioSelectField,
  select: SelectField,
  text: TextField,
};

export const fieldComponents = Object.fromEntries(
  Object.entries(rawFieldComponents).map(([key, Component]) => [key, withHiddenField(Component)]),
);
